import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Copy,
  Check,
  Share2,
  Link,
  TrendingUp,
  DollarSign,
  Clock,
  ExternalLink,
  Search,
  Filter,
  Calendar,
  Eye,
  Users,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthenticatedNavbar from "../components/navigation/AuthenticatedNavbar";

const MyAffiliateLinks = () => {
  const navigate = useNavigate();
  const [affiliateLinks, setAffiliateLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [copiedLinks, setCopiedLinks] = useState(new Set());

  // Mock data - replace with actual API call
  useEffect(() => {
    const fetchAffiliateLinks = async () => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1500));
        
        // Mock data - replace with actual API response
        const mockData = [
          {
            id: 1,
            dealSlug: "asus-tuf-gaming-laptop",
            title: "Asus TUF A17 Gaming Laptop",
            company: "Asus Store",
            image: "https://images.unsplash.com/photo-1593640408182-31c70c8268f5?w=300&h=300&fit=crop",
            originalPrice: 2500,
            discountedPrice: 1999,
            discountPercentage: 20,
            affiliateLink: "https://dealboxx.com/deal/asus-tuf-gaming-laptop?ref=promoter_123456&utm_source=affiliate",
            trackingCode: "AFF123456",
            generatedAt: "2024-01-15T10:30:00Z",
            expiryDate: "2025-10-31T00:00:00Z",
            clicks: 45,
            conversions: 3,
            earnings: 89.97,
            status: "active"
          },
          {
            id: 2,
            dealSlug: "iphone-15-pro-max",
            title: "iPhone 15 Pro Max 256GB",
            company: "Apple Store",
            image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=300&h=300&fit=crop",
            originalPrice: 1200,
            discountedPrice: 1099,
            discountPercentage: 8,
            affiliateLink: "https://dealboxx.com/deal/iphone-15-pro-max?ref=promoter_789012&utm_source=affiliate",
            trackingCode: "AFF789012",
            generatedAt: "2024-01-14T14:20:00Z",
            expiryDate: "2025-12-31T00:00:00Z",
            clicks: 128,
            conversions: 7,
            earnings: 219.80,
            status: "active"
          },
          {
            id: 3,
            dealSlug: "samsung-galaxy-s24",
            title: "Samsung Galaxy S24 Ultra",
            company: "Samsung Store",
            image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=300&h=300&fit=crop",
            originalPrice: 1100,
            discountedPrice: 999,
            discountPercentage: 9,
            affiliateLink: "https://dealboxx.com/deal/samsung-galaxy-s24?ref=promoter_345678&utm_source=affiliate",
            trackingCode: "AFF345678",
            generatedAt: "2024-01-13T09:15:00Z",
            expiryDate: "2025-11-30T00:00:00Z",
            clicks: 67,
            conversions: 2,
            earnings: 59.94,
            status: "expired"
          }
        ];
        
        setAffiliateLinks(mockData);
      } catch (error) {
        toast.error("Failed to load affiliate links");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAffiliateLinks();
  }, []);

  const handleCopyLink = async (link, linkId) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLinks(prev => new Set([...prev, linkId]));
      toast.success("Affiliate link copied to clipboard!");
      setTimeout(() => {
        setCopiedLinks(prev => {
          const newSet = new Set(prev);
          newSet.delete(linkId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysLeft = (expiryDate) => {
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const filteredLinks = affiliateLinks.filter(link => {
    const matchesSearch = link.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         link.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || link.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const totalEarnings = affiliateLinks.reduce((sum, link) => sum + link.earnings, 0);
  const totalClicks = affiliateLinks.reduce((sum, link) => sum + link.clicks, 0);
  const totalConversions = affiliateLinks.reduce((sum, link) => sum + link.conversions, 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
        <AuthenticatedNavbar />
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#F2640F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">Loading your affiliate links...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
      <AuthenticatedNavbar />
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-[#F2640F] transition-all duration-300 rounded-xl hover:bg-white/80 hover:shadow-md group mb-4"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="font-semibold">Back</span>
          </button>
          
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Affiliate Links</h1>
              <p className="mt-2 text-gray-600">Track and manage your generated affiliate links</p>
            </div>
            
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
            >
              <Share2 className="w-5 h-5" />
              Generate New Link
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-4"
        >
          <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold text-gray-900">GH₵{totalEarnings.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Clicks</p>
                <p className="text-2xl font-bold text-gray-900">{totalClicks}</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Conversions</p>
                <p className="text-2xl font-bold text-gray-900">{totalConversions}</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Link className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Links</p>
                <p className="text-2xl font-bold text-gray-900">{affiliateLinks.filter(link => link.status === 'active').length}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6 bg-white rounded-2xl border border-gray-100 shadow-lg p-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Affiliate Links List */}
        {filteredLinks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center py-16"
          >
            <div className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-gray-100 rounded-full">
              <Link className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="mb-4 text-2xl font-bold text-gray-900">
              {affiliateLinks.length === 0 ? "No Affiliate Links Yet" : "No Links Found"}
            </h3>
            <p className="mb-8 text-gray-600 max-w-md mx-auto">
              {affiliateLinks.length === 0 
                ? "You haven't generated any affiliate links yet. Start promoting deals to earn commissions!"
                : "Try adjusting your search or filter criteria to find the links you're looking for."
              }
            </p>
            {affiliateLinks.length === 0 && (
              <button
                onClick={() => navigate("/dashboard")}
                className="px-8 py-4 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              >
                Generate Your First Link
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {filteredLinks.map((link, index) => (
              <motion.div
                key={link.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex gap-4 items-start">
                    {/* Product Image */}
                    <div className="overflow-hidden flex-shrink-0 w-20 h-20 rounded-xl border border-gray-200">
                      <img
                        src={link.image}
                        alt={link.title}
                        className="object-cover w-full h-full"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">
                            {link.title}
                          </h3>
                          <p className="text-gray-600 mb-2">by {link.company}</p>
                          
                          <div className="flex gap-2 items-center mb-3">
                            <span className="px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-full">
                              {link.discountPercentage}% OFF
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              link.status === 'active' 
                                ? 'text-green-600 bg-green-100' 
                                : 'text-red-600 bg-red-100'
                            }`}>
                              {link.status === 'active' ? 'Active' : 'Expired'}
                            </span>
                          </div>

                          <div className="flex gap-4 items-center text-sm text-gray-600">
                            <div className="flex gap-1 items-center">
                              <Calendar className="w-4 h-4" />
                              <span>Generated {formatDate(link.generatedAt)}</span>
                            </div>
                            <div className="flex gap-1 items-center">
                              <Clock className="w-4 h-4" />
                              <span>{getDaysLeft(link.expiryDate)} days left</span>
                            </div>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex gap-4 items-center">
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Clicks</p>
                            <p className="text-lg font-bold text-blue-600">{link.clicks}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Sales</p>
                            <p className="text-lg font-bold text-purple-600">{link.conversions}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Earnings</p>
                            <p className="text-lg font-bold text-green-600">GH₵{link.earnings.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Affiliate Link */}
                      <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                        <div className="flex gap-2 items-center mb-2">
                          <Link className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Affiliate Link:</span>
                          <span className="text-xs font-mono text-gray-500">({link.trackingCode})</span>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={link.affiliateLink}
                            readOnly
                            className="flex-1 px-3 py-2 text-sm font-mono bg-white rounded-lg border border-gray-300"
                          />
                          <button
                            onClick={() => handleCopyLink(link.affiliateLink, link.id)}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                              copiedLinks.has(link.id)
                                ? "bg-green-600 text-white"
                                : "bg-[#F2640F] text-white hover:bg-[#ff6b6b]"
                            }`}
                          >
                            {copiedLinks.has(link.id) ? (
                              <>
                                <Check className="w-4 h-4" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MyAffiliateLinks;
